import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime, type DateInput } from '@f2m3/shared-frontend-ui';
import { useAccountInfo } from '../api/queryHooks/account';

export interface ShopDateFormat {
  /** The account's IANA zone (undefined until account info loads or when unset). */
  timeZone: string | undefined;
  /** Numeric date + 24h time in the shop zone. */
  dateTime: (value: DateInput) => string;
  /** Numeric date in the shop zone. */
  date: (value: DateInput) => string;
}

/**
 * Admin screens that show *when something happened at the shop* (Relations
 * discussions, tasks, events) render in the account's time zone rather than
 * the viewer's. Wraps the shared formatter with `accounts.time_zone`.
 */
export function useShopDateFormat(): ShopDateFormat {
  const { i18n } = useTranslation();
  const { data } = useAccountInfo();
  const timeZone = data?.time_zone;
  return useMemo(
    () => ({
      timeZone,
      dateTime: (value: DateInput) => formatDateTime(value, i18n.language, { timeZone }),
      date: (value: DateInput) => formatDate(value, i18n.language, 'numeric', { timeZone }),
    }),
    [i18n.language, timeZone],
  );
}
